import { useCallback, useEffect, useState } from 'react';
import sessionsApi from 'Api/axios/sessions-api';

/**
 * Single responsibility: own the lifecycle of one session (CRUD + complete toggle).
 * UI components subscribe via the returned mutators; refetch on success is the
 * caller's job (so optimistic flows can choose what to update).
 */
export default function useSessionDetailApi({ sessionId, init = true } = {}) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSession = useCallback(async () => {
    if (!sessionId) return null;
    setLoading(true);
    setError(null);
    const response = await sessionsApi.getById(sessionId);
    if (response.error) {
      setError(response.error);
      setSession(null);
    } else {
      setSession(response.data || null);
    }
    setLoading(false);
    return response;
  }, [sessionId]);

  useEffect(() => {
    if (init && sessionId) fetchSession();
  }, [sessionId, init, fetchSession]);

  const create = useCallback(async payload => sessionsApi.create(payload), []);
  const update = useCallback(async (id, payload) => sessionsApi.update(id, payload), []);
  const softDelete = useCallback(async id => sessionsApi.softDelete(id), []);
  const markComplete = useCallback(async id => sessionsApi.markComplete(id), []);
  const unmarkComplete = useCallback(async id => sessionsApi.unmarkComplete(id), []);

  return {
    session,
    loading,
    error,
    fetchSession,
    create,
    update,
    softDelete,
    markComplete,
    unmarkComplete,
  };
}
